import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import type { Shop } from '../../types/buildExpansion';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ShopStatusBadge } from './ShopStatusBadge';

interface ShopRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialName?: string;
  onSubmitted?: () => void;
}

/** Submit a shop for admin review — replaces open create flow. */
export const ShopRequestModal: React.FC<ShopRequestModalProps> = ({
  isOpen,
  onClose,
  initialName = '',
  onSubmitted,
}) => {
  const { showToast } = useToast();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState('');
  const [whyAdd, setWhyAdd] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [suggestedContact, setSuggestedContact] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [serviceInput, setServiceInput] = useState('');
  const [duplicates, setDuplicates] = useState<Shop[]>([]);
  const [ackDuplicate, setAckDuplicate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setAckDuplicate(false);
      setDuplicates([]);
      setError(null);
    }
  }, [isOpen, initialName]);

  const checkDuplicates = useCallback(async (shopName: string, shopCity: string) => {
    if (shopName.trim().length < 2) {
      setDuplicates([]);
      return;
    }
    try {
      const result = await apiService.checkShopDuplicates(shopName.trim(), shopCity.trim() || undefined);
      setDuplicates(result);
      if (result.length === 0) setAckDuplicate(false);
    } catch {
      setDuplicates([]);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const t = window.setTimeout(() => void checkDuplicates(name, city), 400);
    return () => window.clearTimeout(t);
  }, [isOpen, name, city, checkDuplicates]);

  const addService = () => {
    const value = serviceInput.trim();
    if (!value || services.some((s) => s.toLowerCase() === value.toLowerCase())) return;
    setServices((prev) => [...prev, value]);
    setServiceInput('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Shop name is required.');
      return;
    }
    if (whyAdd.trim().length < 10) {
      setError('Please explain why this shop should be added (at least 10 characters).');
      return;
    }
    if (duplicates.length > 0 && !ackDuplicate) {
      setError('Review possible matches below, or confirm this is a different shop.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiService.submitShopRequest({
        name: trimmedName,
        description: description.trim() || undefined,
        why_add_reason: whyAdd.trim(),
        website_url: websiteUrl.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        suggested_contact: suggestedContact.trim() || undefined,
        evidence_url: evidenceUrl.trim() || undefined,
        services: services.length > 0 ? services : undefined,
        social_links: socialInstagram.trim() ? { instagram: socialInstagram.trim() } : undefined,
        acknowledge_possible_duplicate: ackDuplicate || duplicates.length === 0,
      });
      showToast('Shop request submitted. An admin will review it.', 'success');
      onSubmitted?.();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not submit request.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => undefined : onClose}
      title="Request a shop"
      description="New shops are reviewed before appearing in the directory."
      size="lg"
      closeOnEscape={!submitting}
      closeOnOverlayClick={!submitting}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="alert-error">
            {error}
          </div>
        )}

        {duplicates.length > 0 && (
          <div
            role="region"
            aria-labelledby="dup-heading"
            className="rounded-lg border border-amber-800/60 bg-amber-950/30 p-4"
          >
            <h3 id="dup-heading" className="text-sm font-semibold text-amber-200 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 shrink-0" aria-hidden />
              Possible matches found
            </h3>
            <ul className="mt-2 space-y-2 list-none">
              {duplicates.map((shop) => (
                <li key={shop.id} className="text-sm">
                  <Link to={`/shops/${shop.slug}`} className="text-orange-300 hover:underline">
                    {shop.name}
                  </Link>
                  {[shop.city, shop.state].filter(Boolean).length > 0 && (
                    <span className="text-gray-400 ml-2">
                      {[shop.city, shop.state].filter(Boolean).join(', ')}
                    </span>
                  )}
                  <ShopStatusBadge trustLabel={shop.trust_label ?? 'listed'} compact className="ml-2" />
                </li>
              ))}
            </ul>
            <label className="mt-3 flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={ackDuplicate}
                onChange={(e) => setAckDuplicate(e.target.checked)}
                className="mt-1 checkbox-control"
              />
              This is a different shop — submit anyway
            </label>
          </div>
        )}

        <div>
          <label htmlFor="req-shop-name" className="input-label">
            Shop name <span className="text-orange-300">*</span>
          </label>
          <input
            id="req-shop-name"
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={160}
          />
        </div>

        <div>
          <label htmlFor="req-why" className="input-label">
            Why add this shop? <span className="text-orange-300">*</span>
          </label>
          <textarea
            id="req-why"
            className="input min-h-[72px]"
            value={whyAdd}
            onChange={(e) => setWhyAdd(e.target.value)}
            required
            minLength={10}
            placeholder="e.g. They built my turbo setup and several documented builds credit them."
            rows={2}
          />
        </div>

        <div>
          <label htmlFor="req-description" className="input-label">
            Short description
          </label>
          <textarea
            id="req-description"
            className="input min-h-[72px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="req-city" className="input-label">
              City
            </label>
            <input id="req-city" type="text" className="input" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div>
            <label htmlFor="req-state" className="input-label">
              State / region
            </label>
            <input id="req-state" type="text" className="input" value={state} onChange={(e) => setState(e.target.value)} />
          </div>
        </div>

        <div>
          <label htmlFor="req-website" className="input-label">
            Website
          </label>
          <input
            id="req-website"
            type="url"
            className="input"
            placeholder="https://"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="req-contact" className="input-label">
            Suggested contact
          </label>
          <input
            id="req-contact"
            type="text"
            className="input"
            placeholder="Email, phone, or contact URL"
            value={suggestedContact}
            onChange={(e) => setSuggestedContact(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="req-evidence" className="input-label">
            Supporting evidence (optional)
          </label>
          <input
            id="req-evidence"
            type="url"
            className="input"
            placeholder="Link to portfolio, social post, or invoice"
            value={evidenceUrl}
            onChange={(e) => setEvidenceUrl(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="req-instagram" className="input-label">
            Instagram (optional)
          </label>
          <input
            id="req-instagram"
            type="text"
            className="input"
            placeholder="@handle or URL"
            value={socialInstagram}
            onChange={(e) => setSocialInstagram(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="req-service" className="input-label">
            Specialties / services
          </label>
          <div className="flex gap-2">
            <input
              id="req-service"
              type="text"
              className="input flex-1"
              placeholder="e.g. Tuning, Fabrication"
              value={serviceInput}
              onChange={(e) => setServiceInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addService();
                }
              }}
            />
            <Button type="button" variant="secondary" onClick={addService} disabled={!serviceInput.trim()}>
              Add
            </Button>
          </div>
          {services.length > 0 && (
            <ul className="flex flex-wrap gap-1.5 mt-2" aria-label="Added services">
              {services.map((service) => (
                <li
                  key={service}
                  className="badge bg-[#111] text-gray-200 border border-white/[0.08] inline-flex items-center gap-1"
                >
                  {service}
                  <button
                    type="button"
                    onClick={() => setServices((prev) => prev.filter((s) => s !== service))}
                    className="rounded p-0.5 hover:text-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
                    aria-label={`Remove ${service}`}
                  >
                    <XMarkIcon className="w-3.5 h-3.5" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={submitting} loadingLabel="Submitting…">
            Submit request
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ShopRequestModal;
